/**
 * Forecast "Delivery timeline" (23 #3) — replaces the monthly bar that added pieces of different parts
 * together. One lane per LPO on a month axis: the expected date as a dot, a dashed line to Today with
 * "202 d late · 60 pcs left", the late zone shaded before Today, and the next 90 days said in words when
 * nothing is scheduled. Pure HTML/CSS (percent positions), so it scales with the card.
 */
import { useEffect, useState } from 'react';
import { CalendarX } from 'lucide-react';
import { formatDate, formatNumber, toDateInputValue } from '@/lib/format';
import { cn } from '@/lib/utils';
import { CardHead, SfCard, plural } from './SfUi';
import { buildTimeline } from './forecastModel';

function Legend() {
  return (
    <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-ink-3">
      <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-status-red" aria-hidden="true" />Late</span>
      <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-brand" aria-hidden="true" />Expected</span>
      <span className="inline-flex items-center gap-1.5"><span className="h-3 w-0.5 rounded bg-lime-600" aria-hidden="true" />Today</span>
    </span>
  );
}

/** [callback ref, width in px] — 0 until measured, or where ResizeObserver is missing. */
function useWidth() {
  const [node, setNode] = useState(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.round(entry.contentRect.width)));
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);
  return [setNode, width];
}

// A chip centred between Today and a date only a few days away would cover the date's dot and straddle the Today
// line (an upcoming LPO would look late). Below this share of the plot the chip is anchored beside the dot instead.
const SHORT_SPAN_PCT = 12;

/** Where a lane's chip goes: 'centre' (between dot and Today), 'right' / 'left' of the dot. Exported for tests. */
export function chipPlacement(lane, todayPct) {
  const from = lane.late ? lane.pct : todayPct;
  const to = lane.late ? todayPct : lane.pct;
  if (lane.late) return to - from < SHORT_SPAN_PCT ? 'left' : 'centre';
  // Upcoming: right of the dot, in the future zone — unless it would run off the plot and there is room before it.
  return lane.pct > 78 && lane.pct - todayPct > 22 ? 'left' : 'right';
}

const CHIP_STYLE = {
  centre: (lane, from, to) => ({ left: `${(from + to) / 2}%`, transform: 'translate(-50%, -50%)' }),
  right: (lane) => ({ left: `${lane.pct}%`, transform: 'translate(10px, -50%)' }),
  left: (lane) => ({ left: `${lane.pct}%`, transform: 'translate(calc(-100% - 10px), -50%)' }),
};

function Lane({ lane, todayPct }) {
  const from = lane.late ? lane.pct : todayPct;
  const to = lane.late ? todayPct : lane.pct;
  const placement = chipPlacement(lane, todayPct);
  const chip = lane.late
    ? <><b className="font-bold text-status-red-ink">{formatNumber(lane.daysLate)} d late</b> · {formatNumber(lane.remaining)} pcs {lane.delivered > 0 ? 'left' : ''}</>
    : <><b className="font-bold text-ink">in {formatNumber(lane.daysUntil)} d</b> · {formatNumber(lane.remaining)} pcs</>;
  return (
    <div className="relative h-10" data-testid={`timeline-lane-${lane.lpoNumber}`}>
      {to > from ? (
        <span
          className={cn('absolute top-1/2 h-0 -translate-y-1/2 border-t-2 border-dashed', lane.late ? 'border-status-red' : 'border-brand')}
          style={{ left: `${from}%`, width: `${to - from}%` }}
          aria-hidden="true"
        />
      ) : null}
      <span
        className={cn('absolute top-1/2 z-[2] h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-surface', lane.late ? 'bg-status-red' : 'bg-brand')}
        style={{ left: `${lane.pct}%` }}
        title={`${lane.lpoNumber} expected ${formatDate(lane.expected)}${lane.clipped === 'start' ? ' (before this range)' : ''}`}
        aria-hidden="true"
      />
      <span
        className="absolute top-1/2 z-[1] whitespace-nowrap rounded-full border border-line bg-surface px-2 py-0.5 text-[11.5px] tabular-nums text-ink-3 shadow-card"
        style={CHIP_STYLE[placement](lane, from, to)}
        data-placement={placement}
        data-testid={`timeline-chip-${lane.lpoNumber}`}
      >
        {chip}
      </span>
    </div>
  );
}

/** props: lines (normalizeLines of the current scope), today ('YYYY-MM-DD', defaults to today in Dubai). */
export function DeliveryTimeline({ lines, today }) {
  const [plotRef, plotWidth] = useWidth();
  const day = today || toDateInputValue(new Date());
  const timeline = buildTimeline(lines, { today: day });
  if (!timeline) return null;
  const { months, todayPct, lanes, hidden, undated, nextWindowEmpty } = timeline;
  const first = months[0];
  const last = months[months.length - 1];
  const range = first && last ? `${first.label}${first.year !== last.year ? ` ${first.year}` : ''} – ${last.label} ${last.year}` : '';
  const lateCount = lanes.filter((lane) => lane.late).length;
  const showYear = !plotWidth || plotWidth / Math.max(1, months.length) >= 56;

  return (
    <SfCard data-testid="delivery-timeline">
      <CardHead title="Delivery timeline" sub={`Expected date per LPO · ${range}`} right={<Legend />} />
      <div className="overflow-x-auto">
        <div className="min-w-[640px] px-4 pb-4 pt-3 sm:px-5">
          {lanes.length ? (
            <div className="grid grid-cols-[150px_minmax(0,1fr)] gap-x-3 md:grid-cols-[180px_minmax(0,1fr)]">
              {/* month axis */}
              <div />
              <div ref={plotRef} className="relative h-11" data-testid="timeline-axis">
                {months.map((month, index) => (
                  <span key={month.key} className="absolute bottom-1 whitespace-nowrap pl-1.5 text-[11.5px] text-ink-3" style={{ left: `${(index / months.length) * 100}%` }}>
                    {month.label}{showYear && (index === 0 || month.label === 'Jan') ? <span className="text-ink-4"> {month.year}</span> : null}
                  </span>
                ))}
                <span
                  className="absolute top-0 z-[2] -translate-x-1/2 whitespace-nowrap rounded-full bg-brand px-2 py-0.5 text-[11px] font-semibold text-white"
                  data-testid="timeline-today"
                  style={{ left: `${todayPct}%` }}
                >
                  Today · {formatDate(day, { year: 'never' })}
                </span>
              </div>

              {/* lane labels */}
              <ul className="pt-4">
                {lanes.map((lane) => (
                  <li key={lane.key} className="flex h-10 min-w-0 flex-col justify-center">
                    <span className="truncate font-mono text-[12.5px] font-bold text-ink">{lane.lpoNumber || 'No LPO number'}</span>
                    <span className="truncate text-[11.5px] text-ink-3">{lane.supplier || 'Supplier missing'} · {plural(lane.lines, 'line')}</span>
                  </li>
                ))}
              </ul>

              {/* plot */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 rounded-l-lg bg-status-red-bg" style={{ width: `${todayPct}%` }} aria-hidden="true" />
                {lateCount ? (
                  <span className="absolute left-2 top-0.5 z-[1] text-[10.5px] font-bold uppercase tracking-[0.05em] text-status-red-ink">Late — past expected date</span>
                ) : null}
                {months.map((month, index) => (
                  <span key={month.key} className="absolute inset-y-0 w-px bg-[color:var(--line-2)]" style={{ left: `${(index / months.length) * 100}%` }} aria-hidden="true" />
                ))}
                <span className="absolute -top-1 bottom-0 z-[1] w-0.5 -translate-x-1/2 bg-lime-600" style={{ left: `${todayPct}%` }} aria-hidden="true" />
                {nextWindowEmpty ? (
                  <span
                    className="absolute top-1/2 z-[1] flex -translate-y-1/2 items-center justify-center gap-1.5 px-2 text-center text-[12.5px] text-ink-3"
                    style={{ left: `${todayPct}%`, right: 0 }}
                    data-testid="timeline-next-90-empty"
                  >
                    <CalendarX className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    No deliveries scheduled in the next 90 days
                  </span>
                ) : null}
                <div className="relative z-[2] pt-4">
                  {lanes.map((lane) => <Lane key={lane.key} lane={lane} todayPct={todayPct} />)}
                </div>
              </div>
            </div>
          ) : (
            <p className="flex items-center gap-2 py-6 text-[13px] text-ink-3"><CalendarX className="h-4 w-4" aria-hidden="true" />No LPO in this view has an expected date.</p>
          )}
          {hidden || undated ? (
            <p className="mt-2 text-[12px] text-ink-3">
              {hidden ? `${plural(hidden, 'more LPO')} not drawn — see the table below.` : ''}
              {hidden && undated ? ' ' : ''}
              {undated ? `${plural(undated, 'LPO')} without an expected date.` : ''}
            </p>
          ) : null}
        </div>
      </div>
    </SfCard>
  );
}
